/**
 * The field crew's trunks and surveyed areas, from the bound field annotations' own
 * `field_annotations.gpkg` (forun-api.md §5.6). The file is small, so it is read whole when the
 * page opens, not with the first pick as the crown files are.
 */
import { useEffect, useState } from 'react';
import type { FeatureCollection } from 'geojson';
import { FIELD_TABLES } from '@/map/layers';
import type { LinkSource } from '@/map/linkSource';
import { loadLinkedGpkgLayers, type LinkedLayer } from '@/map/vectorSource';

export interface FieldData {
  trunks: FeatureCollection | null;
  areas: FeatureCollection | null;
  loading: boolean;
  /** The file could not be read, or holds no trunk table. */
  failed: boolean;
}

/** Nothing while no field annotations are bound or the map is off; `file` names the GeoPackage the grant gives and its digest. */
export function useFieldData(file: LinkedLayer | undefined, links: LinkSource, enabled: boolean): FieldData {
  const key = file && enabled ? JSON.stringify([file.object, file.path, file.digest]) : '';
  const [state, setState] = useState<{ key: string; data: FieldData } | null>(null);
  useEffect(() => {
    if (!key) return;
    const [object, path, digest] = JSON.parse(key) as [string, string, string];
    let alive = true;
    (path ? loadLinkedGpkgLayers({ object, path, digest }, links, [FIELD_TABLES.trunk, FIELD_TABLES.area]) : Promise.reject(new Error('no GeoPackage'))).then(
      (t) => alive && setState({ key, data: { trunks: t[FIELD_TABLES.trunk] ?? null, areas: t[FIELD_TABLES.area] ?? null, loading: false, failed: !t[FIELD_TABLES.trunk] } }),
      () => alive && setState({ key, data: { trunks: null, areas: null, loading: false, failed: true } }),
    );
    return () => {
      alive = false;
    };
  }, [key, links]);
  if (!key) return { trunks: null, areas: null, loading: false, failed: false };
  return state && state.key === key ? state.data : { trunks: null, areas: null, loading: true, failed: false };
}
